// React
import { useEffect, useState   } from "react";
import { useHistory, useParams } from "react-router-dom";

// Hooks
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useMetaDescription } from "../../hooks/useMetaDescription";

// Librarys
import toast, { Toaster } from 'react-hot-toast';
import * as yup from 'yup';
import { Oval } from 'react-loader-spinner';
import getValidationErrors from "../../utility/validationErrors";

//Services
import api from "../../services/api";

// Styles
import { Container, Header, Main, FormContainer, FormContent, CheckboxContainer, ButtonContainer } from "./styles";

// Components
import Checkbox from "../../components/form/InputCheckbox";
import InputDefault from "../../components/form/InputDefault";

//Icons
import { 
    IconEyeOpen, 
    IconClosedEye,
    IconEasyJurLegalOps, 
} from "../../components/icons";
import { comum } from "../../styles/Themes/light";
import { toastDefs, toastDefsError } from '../../config/toastConfig';
import { normalizeCnpj } from '../../utils/masks';

interface Errors {
    tipo_pessoa?: string,
    nome?: string,
    email?: string,
    cpf?: string,
    telefone?: string,
    cnpj?: string,
    razao_social?: string,
    senha?: string,
    confirmeSenha?: string,
    termos?: string,
}

export default function RegisterSolicitante() {
    // Define o título e meta description da página
    useDocumentTitle({ title: "Registro de Solicitante" });
    useMetaDescription({ 
        description: "Complete seu registro como solicitante na EasyJur Legal Ops. Acesse oportunidades jurídicas personalizadas." 
    });

    const params = useParams<any>();
    const history = useHistory();
    const token = params.id; // Token recebido via URL como /registro-solicitante/:id
    
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ changeVisibility, setChangeVisibility ] = useState<boolean>(true);
    const [ changeVisibilityConfirm, setChangeVisibilityConfirm ] = useState<boolean>(true);
    const [ checked, setChecked ] = useState<boolean>(false);
    const [ tipoPessoa, setTipoPessoa ] = useState<string>('juridica');
    const [ advancePageError, setAdvancePageError ] = useState<Errors>({});

    const [DTOUser, setDTOUser] = useState({
        token: token,
        tipo_pessoa: 'juridica',
        nome: '',
        email: '',
        cpf: '',
        telefone: '',
        cnpj: '',
        razao_social: '',
        senha: '',
        confirmeSenha: '',
        termos: false,
        juridica: false,
    });

    function toasterror(message: string) {
            toast.error(message, toastDefsError);
    }

    useEffect(() => {
        // Validar token quando a página carregar
        if (token) {
            validateToken();
        } else {
            toasterror('Token de registro inválido');
            // Redirecionar para página de erro ou login
            setTimeout(() => {
                history.push('/login');
            }, 3000);
        }
    }, [token]);

    async function validateToken() {
        try {
            const response = await api.get(`/escritorio/token/${token}`);
            if (response.data.status !== 'success') {
                throw new Error('Token inválido ou expirado');
            }

            const escritorio = response.data.result;
            setDTOUser({
                token: token,
                tipo_pessoa: DTOUser.tipo_pessoa,
                nome: escritorio.nome,
                email: escritorio.email,
                cpf: DTOUser.cpf,
                telefone: DTOUser.telefone,
                cnpj: DTOUser.cnpj,
                razao_social: DTOUser.razao_social,
                senha: DTOUser.senha,
                confirmeSenha: DTOUser.confirmeSenha,
                juridica: DTOUser.juridica,
                termos: DTOUser.termos,
            });
            
        } catch (error: any) {
            toasterror('Token inválido ou expirado');
            setTimeout(() => {
                history.push('/login');
            }, 3000);
        }
    }

    function handleOnChange(name: string, value: any) {
        const newDTO: any = { ...DTOUser };
        
        if (name === 'cpf' || name === 'telefone') {
            // Remove caracteres não numéricos
            let newValue = value.replace(/\D/g, '');
            newDTO[name] = newValue;
        } else if (name === 'cnpj') {
            let newValue = normalizeCnpj(value);
            newDTO[name] = newValue;
        } else {
            newDTO[name] = value;
        }
        
        setDTOUser(newDTO);
    }

    function handleTipoPessoaChange(tipo: string) {
        setTipoPessoa(tipo);
        
        const newDTO: any = { ...DTOUser };
        newDTO.tipo_pessoa = tipo;
        
        if (tipo === 'fisica') {
            newDTO.cnpj = '';
            newDTO.razao_social = '';
            setAdvancePageError(prev => ({
                ...prev,
                cnpj: undefined,
                razao_social: undefined
            }));
        } else {
            newDTO.cpf = '';
        }

        setDTOUser(newDTO);
    }

    async function validateForm() {
            const schema = yup.object().shape({
                tipo_pessoa: yup.string().required('Tipo de pessoa é obrigatório'),
                nome: yup.string().required('Nome é obrigatório'),
                email: yup.string().required('Email é obrigatório').email('Digite um email válido'),
                senha: yup.string()
                    .required('Senha é obrigatória')
                    .min(6, 'Senha deve ter pelo menos 6 caracteres')
                    .matches(
                        /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/,
                        "Senha deve conter pelo menos 1 maiúscula, 1 número e 1 caractere especial"
                    ),
                confirmeSenha: yup.string()
                    .required('Confirmação de senha é obrigatória')
                    .oneOf([yup.ref('senha')], 'As senhas não conferem!'),
                telefone: yup.string().required('Telefone é obrigatório').min(10, 'Telefone inválido'),
                cpf: yup.string().required('CPF é obrigatório').min(11, 'CPF inválido').max(14, 'CPF inválido'),
                termos: yup.boolean().oneOf([true], 'É necessário aceitar os termos de uso'),
                cnpj: yup.string().when('tipo_pessoa', {
                    is: 'juridica',
                    then: yup.string().required('CNPJ é obrigatório').matches(/^[0-9A-Z]{14}$/, 'CNPJ inválido'),
                    otherwise: yup.string().notRequired(),
                }),
                razao_social: yup.string().when('tipo_pessoa', {
                    is: 'juridica',
                    then: yup.string().required('Razão Social é obrigatória'),
                    otherwise: yup.string().notRequired(),
                }),
            });
            await schema.validate(DTOUser, {
                abortEarly: false,
            });
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        
        try {
            setLoading(true);
            await validateForm();
            const data = {
                ...DTOUser,
                juridica: tipoPessoa !== 'fisica'
            }
            const response = await api.put('/escritorio/confirma/' + token, data);

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Erro ao realizar cadastro');
            }


            toast.success('Cadastro realizado com sucesso!', toastDefs);

            setTimeout(() => {
                history.push('/login');
            }, 3000);

        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                const errors = getValidationErrors(err);
                setAdvancePageError(errors);

                toast.error('Preencha os campos corretamente.', toastDefsError);
            } else {
                toast.error(err.response.data.message || 'Erro ao realizar cadastro', toastDefsError);
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <Container>
            {
                loading &&
                <Oval
                    height={150}
                    width={150}
                    color={comum.colors.primaryRed400}
                    wrapperStyle={{}}
                    wrapperClass="loader"
                    visible={true}
                    ariaLabel='oval-loading'
                    secondaryColor={comum.colors.primaryRed400}
                    strokeWidth={2}
                    strokeWidthSecondary={2}
                />
            }
            <Header>
                <div className="flex items-center gap-3 justify-between w-full">
                    <div className="flex flex-col gap-0.5">
                        <h1 className="text-lg font-bold leading-none">Cadastro de Escritório Solicitante</h1>
                        <p className="text-xs font-medium leading-none">Preencha as informações para cadastrar seu escritório</p>
                    </div>
                    <div className="flex items-center logo-icon">
                        <IconEasyJurLegalOps />
                    </div>
                </div>
            </Header>

            <Main>
                <FormContainer>
                    <FormContent tipoPessoa={tipoPessoa}>
                        <form onSubmit={handleSubmit}>
                            <div className="form-section">
                                <h2>Informações do Escritório</h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Tipo de pessoa:</label>
                                        <div className="flex gap-6 mt-2 md:gap-6 gap-4">
                                            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-500">
                                                <input
                                                    type="radio"
                                                    name="tipo_pessoa"
                                                    value="fisica"
                                                    checked={tipoPessoa === 'fisica'}
                                                    onChange={() => handleTipoPessoaChange('fisica')}
                                                    className="w-4 h-4 accent-red-500 cursor-pointer"
                                                />
                                                <span className="font-normal">Pessoa física</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-500">
                                                <input
                                                    type="radio"
                                                    name="tipo_pessoa"
                                                    value="juridica"
                                                    checked={tipoPessoa === 'juridica'}
                                                    onChange={() => handleTipoPessoaChange('juridica')}
                                                    className="w-4 h-4 accent-red-500 cursor-pointer"
                                                />
                                                <span className="font-normal">Pessoa jurídica</span>
                                            </label>
                                        </div>
                                        {advancePageError.tipo_pessoa && (
                                            <span className="text-red-500 text-xs mt-1 block">{advancePageError.tipo_pessoa}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div>
                                        <InputDefault
                                            title="Seu nome completo: "
                                            name="nome"
                                            placeholder="Digite seu nome completo"
                                            value={DTOUser.nome}
                                            onChangeText={(value) => handleOnChange('nome', value)}
                                            className={advancePageError.nome ? 'error' : ''}
                                            error={advancePageError.nome}
                                            obrigatorio={true}
                                        />
                                    </div>
                                    <div>
                                        <InputDefault
                                            title="CPF: "
                                            name="cpf"
                                            mask="999.999.999-99"
                                            placeholder="000.000.000-00"
                                            value={DTOUser.cpf}
                                            onChangeText={(value) => handleOnChange('cpf', value)}
                                            className={advancePageError.cpf ? 'error' : ''}
                                            error={advancePageError.cpf}
                                            obrigatorio={true}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div>
                                        <InputDefault
                                            title="E-mail para contato: "
                                            name="email"
                                            type="email"
                                            placeholder="seu@email.com"
                                            value={DTOUser.email}
                                            onChangeText={(value) => handleOnChange('email', value)}
                                            className={advancePageError.email ? 'error' : ''}
                                            error={advancePageError.email}
                                            obrigatorio={true}
                                        />
                                    </div>
                                    <div>
                                        <InputDefault
                                            title="Telefone contato: "
                                            name="telefone"
                                            mask="(99) 99999-9999"
                                            placeholder="(00) 00000-0000"
                                            value={DTOUser.telefone}
                                            onChangeText={(value) => handleOnChange('telefone', value)}
                                            className={advancePageError.telefone ? 'error' : ''}
                                            error={advancePageError.telefone}
                                            obrigatorio={true}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div>
                                        <div className="relative">
                                            <InputDefault
                                                title="Senha: "
                                                name="senha"
                                                type={changeVisibility ? 'password' : 'text'}
                                                placeholder="Digite sua senha"
                                                value={DTOUser.senha}
                                                onChangeText={(value) => handleOnChange('senha', value)}
                                                className={advancePageError.senha ? 'error' : ''}
                                                error={advancePageError.senha}
                                                obrigatorio={true}
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 flex items-center text-gray-500"
                                                style={{ top: '40px', transform: 'translateY(-60%)' }}
                                                onClick={() => setChangeVisibility(!changeVisibility)}
                                                tabIndex={-1}
                                            >
                                                {changeVisibility ? <IconEyeOpen /> : <IconClosedEye />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="relative">
                                            <InputDefault
                                                title="Confirmar senha: "
                                                name="confirmeSenha"
                                                type={changeVisibilityConfirm ? 'password' : 'text'}
                                                placeholder="Confirme sua senha"
                                                value={DTOUser.confirmeSenha}
                                                onChangeText={(value) => handleOnChange('confirmeSenha', value)}
                                                className={advancePageError.confirmeSenha ? 'error' : ''}
                                                error={advancePageError.confirmeSenha}
                                                obrigatorio={true}
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-3 flex items-center text-gray-500"
                                                style={{ top: '40px', transform: 'translateY(-60%)' }}
                                                onClick={() => setChangeVisibilityConfirm(!changeVisibilityConfirm)}
                                                tabIndex={-1}
                                            >
                                                {changeVisibilityConfirm ? <IconEyeOpen /> : <IconClosedEye />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {tipoPessoa === 'juridica' ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                        <div>
                                            <InputDefault
                                                title="Razão Social: "
                                                name="razao_social"
                                                placeholder="Digite a razão social"
                                                value={DTOUser.razao_social}
                                                onChangeText={(value) => handleOnChange('razao_social', value)}
                                                className={advancePageError.razao_social ? 'error' : ''}
                                                error={advancePageError.razao_social}
                                                obrigatorio={true}
                                            />
                                        </div>
                                        <div>
                                            <InputDefault
                                                title="CNPJ: "
                                                name="cnpj"
                                                mask="**.***.***/****-99"
                                                placeholder="00.000.000/0000-00"
                                                value={DTOUser.cnpj}
                                                onChangeText={(value) => handleOnChange('cnpj', value)}
                                                className={advancePageError.cnpj ? 'error' : ''}
                                                error={advancePageError.cnpj}
                                                obrigatorio={true}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mb-4"></div>
                                )}


                                <CheckboxContainer>
                                    <Checkbox
                                        label="Concordo com os "
                                        name="termos"
                                        value={checked}
                                        onClick={() => {
                                            setChecked(!checked);
                                            handleOnChange('termos', !checked);
                                        }}
                                        link="https://easyjur.com/termos-de-uso-legal-operations/"
                                        linkText="Termos de uso e condições"
                                    />
                                    {advancePageError.termos && (
                                        <span className="error">{advancePageError.termos}</span>
                                    )}
                                </CheckboxContainer>

                                <ButtonContainer>
                                    <button 
                                        type="submit"
                                        disabled={loading}
                                        className="submit-button"
                                    >
                                        {loading ? 'Cadastrando...' : 'Cadastrar Escritório'}
                                    </button>
                                </ButtonContainer>
                            </div>
                        </form>
                    </FormContent>
                </FormContainer>
            </Main>

        </Container>
    );
} 